import { Link } from 'react-router-dom';
import { Phone, Mail, MessageCircle, MapPin } from 'lucide-react';
import { useState, useEffect } from 'react';
import { getHQPartnerSettings, HQPartnerSettings } from '../services/partnerService';
import { SOCIAL_LINKS, CONTACT_INFO, FOOTER_COLUMNS } from '../data/siteContent';
import { AppStoreBadge, GooglePlayBadge } from './shared/StoreBadges';
import { useWebsiteContent } from '../contexts/WebsiteContentContext';
import type { CmsFooterColumns, CmsFooterColumn } from '../types/websiteCms.types';

const DEFAULT_FOOTER_COLUMNS: CmsFooterColumns = {
  company: FOOTER_COLUMNS.company.map(c => ({ ...c })),
  freeTools: FOOTER_COLUMNS.freeTools.map(c => ({ label: c.label, path: c.path, ...('external' in c ? { external: true } : {}) })),
  services: FOOTER_COLUMNS.services.map(c => ({ ...c })),
};

/** Ensure specific items always appear in a column, even when CMS overrides defaults. */
function ensureItems(column: CmsFooterColumn[], required: CmsFooterColumn[]): CmsFooterColumn[] {
  const existing = new Set(column.map(c => c.path));
  const merged = [...column];
  for (const item of required) {
    if (!existing.has(item.path)) merged.push(item);
  }
  return merged;
}

function FooterLink({ item }: { item: CmsFooterColumn }) {
  if (item.external) {
    return (
      <a href={item.path} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-primary transition">
        {item.label}
      </a>
    );
  }
  return (
    <Link to={item.path} className="text-gray-600 hover:text-primary transition">
      {item.label}
    </Link>
  );
}

export default function Footer() {
  const [hqSettings, setHqSettings] = useState<HQPartnerSettings | null>(null);
  const cms = useWebsiteContent();
  const socialLinks = cms?.socialLinks ?? SOCIAL_LINKS;
  const contactInfo = cms?.contactInfo ?? CONTACT_INFO;
  const rawFooter = cms?.footerColumns ?? DEFAULT_FOOTER_COLUMNS;

  // Ensure new items show even when CMS provides an older snapshot.
  // (The old sitewide WHATSAPP CHANNEL link is gone on purpose — /whatsapp is
  // just a client-side redirect to /contactus.)
  const company = ensureItems(rawFooter.company, [
    { label: 'HOME', path: '/' },
    { label: 'PROPERTY INVESTMENT DEALS', path: '/best-property-investment-opportunities' },
    { label: 'EDUCATION', path: '/education' },
  ]);
  const freeTools = ensureItems(rawFooter.freeTools, [
    { label: 'AFFORDABILITY CALCULATOR', path: '/tools/affordability' },
    { label: 'FREE CREDIT CHECK', path: '/tools/credit-check' },
    { label: 'TAX SAVINGS CALCULATOR', path: '/tools/tax-savings' },
    { label: 'MOBILE APP', path: '/mobile-app' },
  ]);
  const services = rawFooter.services ?? DEFAULT_FOOTER_COLUMNS.services ?? [];

  useEffect(() => {
    const loadHQSettings = async () => {
      const settings = await getHQPartnerSettings();
      setHqSettings(settings);
    };
    loadHQSettings();
  }, []);

  return (
    <footer className="bg-white border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
          {/* Column 1: Logo & Contact (span 2 on lg) */}
          <div className="col-span-2">
            {hqSettings?.logoUrl ? (
              <img
                src={hqSettings.logoUrl}
                alt={hqSettings.name || 'PropInvest\u2122'}
                width={160}
                height={40}
                className="h-10 w-auto mb-4"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div className="text-2xl font-extrabold mb-4 tracking-tight">
                <span className="text-gray-900">PROP</span>
                <span className="text-primary">INVEST</span>
                <span className="text-gray-400 text-xs align-super ml-0.5">&#8482;</span>
              </div>
            )}
            <ul className="space-y-3 text-sm">
              <li>
                <a href={contactInfo.phoneLink} className="flex items-center gap-2 text-gray-600 hover:text-primary transition">
                  <Phone size={16} className="text-primary flex-shrink-0" />
                  Call {contactInfo.phone}
                </a>
              </li>
              <li>
                <a href={contactInfo.whatsappLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-600 hover:text-primary transition">
                  <MessageCircle size={16} className="text-primary flex-shrink-0" />
                  WhatsApp {contactInfo.whatsappPhone ?? CONTACT_INFO.whatsappPhone}
                </a>
              </li>
              <li>
                <a href={contactInfo.emailLink} className="flex items-center gap-2 text-gray-600 hover:text-primary transition">
                  <Mail size={16} className="text-primary flex-shrink-0" />
                  {contactInfo.email}
                </a>
              </li>
              <li>
                <span className="flex items-start gap-2 text-gray-600">
                  <MapPin size={16} className="text-primary flex-shrink-0 mt-0.5" />
                  Zomerlust Estate, Second Floor, Cnr Bergriver Boulevard &amp; Bergsig Avenue, Paarl, Cape Town, 7646
                </span>
              </li>
            </ul>

            {/* Regulatory Credentials */}
            <div className="mt-5 pt-4 border-t border-gray-100">
              <p className="text-xs font-bold text-gray-900 tracking-wide mb-2">REGULATORY CREDENTIALS</p>
              <p className="text-xs text-gray-500 leading-relaxed">
                PropInvest 5 (Pty) Ltd<br />
                Registered with the PPRA
              </p>
            </div>

            {/* Logos */}
            <div className="mt-4 flex items-center gap-3">
              <img
                src="/images/proudly-sa.png"
                alt="Proudly South African"
                width={64}
                height={64}
                loading="lazy"
                decoding="async"
                className="w-16 h-16"
              />
              <img
                src="/images/ppra-logo.png"
                alt="PPRA - Property Practitioners Regulatory Authority"
                width={64}
                height={64}
                loading="lazy"
                decoding="async"
                className="w-16 h-16 object-contain"
              />
            </div>
          </div>

          {/* Column 2: Company */}
          <div>
            <h3 className="font-bold text-gray-900 mb-4 text-sm tracking-wide">COMPANY</h3>
            <ul className="space-y-3 text-sm">
              {company.map((item) => (
                <li key={item.path}><FooterLink item={item} /></li>
              ))}
            </ul>
          </div>

          {/* Column 3: Free Tools */}
          <div>
            <h3 className="font-bold text-gray-900 mb-4 text-sm tracking-wide">FREE TOOLS</h3>
            <ul className="space-y-3 text-sm">
              {freeTools.map((item) => (
                <li key={item.path}><FooterLink item={item} /></li>
              ))}
            </ul>
          </div>

          {/* Column 4: Services */}
          <div>
            <h3 className="font-bold text-gray-900 mb-4 text-sm tracking-wide">SERVICES</h3>
            <ul className="space-y-3 text-sm">
              {services.map((item) => (
                <li key={item.path}><FooterLink item={item} /></li>
              ))}
            </ul>
          </div>

          {/* Column 5: App Downloads */}
          <div>
            <h3 className="font-bold text-gray-900 mb-4 text-sm tracking-wide">GET THE APP</h3>
            <div className="flex flex-col gap-3">
              <Link
                to="/mobile-app"
                className="block bg-primary hover:bg-primary-dark text-white text-center text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors"
              >
                Download the App
              </Link>
              <div><AppStoreBadge className="h-10 w-full" /></div>
              <div><GooglePlayBadge className="h-10 w-full" /></div>
            </div>
          </div>
        </div>

        {/* Social Icons */}
        <div className="flex justify-center gap-4 mt-10 pt-8 border-t border-gray-200">
          <span className="text-sm text-gray-500 mr-2">FOLLOW US</span>
          <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-primary transition" aria-label="Facebook">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          </a>
          <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-primary transition" aria-label="Instagram">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
          </a>
          <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-primary transition" aria-label="X (Twitter)">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
          </a>
          <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-primary transition" aria-label="LinkedIn">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
          </a>
          <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-primary transition" aria-label="YouTube">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
          </a>
          <a href={socialLinks.whatsapp} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-primary transition" aria-label="WhatsApp">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          </a>
        </div>

        {/* Copyright Bar */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <p className="text-sm text-gray-500 mb-2">
            Copyright {new Date().getFullYear()} Propinvest 5 (Pty) Ltd. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-2 text-xs text-gray-400">
            <a href="/documents/privacy_policy.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition">PRIVACY POLICY</a>
            <span>|</span>
            <a href="/documents/paia_manual.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition">PAIA MANUAL</a>
            <span>|</span>
            <a href="/documents/terms_of_use.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition">TERMS OF USE</a>
            <span>|</span>
            <a href="/documents/cookies_policy.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition">COOKIES POLICY</a>
            <span>|</span>
            <a href="/delete-account" className="hover:text-primary transition">DELETE ACCOUNT</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
